import React, { useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
interface ExitIntentPopupProps {
  onAccept: () => void;
}
export function ExitIntentPopup({
  onAccept
}: ExitIntentPopupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const hasShownRef = useRef(false);
  // Set up triggers ONCE on mount
  useEffect(() => {
    const trigger = () => {
      if (hasShownRef.current) return;
      hasShownRef.current = true;
      setIsOpen(true);
    };
    // Desktop: mouse leaves through the top of the viewport
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) trigger();
    };
    // Mobile: intercept back button via history state
    const handlePopState = () => {
      if (!hasShownRef.current) {
        // Push state again so the user stays on the page
        window.history.pushState({
          exitIntent: true
        }, '', window.location.href);
        trigger();
      }
    };
    // Push an initial history entry so first "back" fires popstate
    window.history.pushState({
      exitIntent: true
    }, '', window.location.href);
    document.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('popstate', handlePopState);
    // Mobile fallback: trigger after rapid scroll-to-top (user reaching for back/close)
    let lastScrollY = window.scrollY;
    let scrollUpDistance = 0;
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY < lastScrollY) {
        scrollUpDistance += lastScrollY - currentY;
        if (scrollUpDistance > 250 && currentY < 50) {
          trigger();
        }
      } else {
        scrollUpDistance = 0;
      }
      lastScrollY = currentY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // Lock scroll + ESC key while open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);
  if (!isOpen) return null;
  const handleAccept = () => {
    setIsOpen(false);
    onAccept();
  };
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 animate-fadeIn" role="dialog" aria-modal="true" aria-labelledby="exit-intent-title" data-id="element-1">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 md:p-8 relative text-center" data-id="element-2">
        <button onClick={() => setIsOpen(false)} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 transition-colors" aria-label="Close" data-id="element-3">
          <X size={22} data-id="element-4" />
        </button>

        <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-[#E53935]" data-id="element-5">
          Wait! Don't leave yet
        </div>

        <h2 id="exit-intent-title" className="text-xl md:text-2xl font-bold text-black mb-6 leading-snug" data-id="element-6">
          You're One <span className="text-[#43A047]" data-id="element-7">(Free)</span> Quote Away
          From Saving Over <span className="text-[#43A047]" data-id="element-8">$14,000</span> On
          Average On Your Bathroom Remodel
        </h2>

        <button onClick={handleAccept} className="w-full py-4 bg-[#43A047] hover:bg-[#388E3C] text-white font-bold text-lg md:text-xl rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2" data-id="element-9">
          Take Me To The Quote →
        </button>

        <button onClick={() => setIsOpen(false)} className="mt-3 text-xs text-gray-500 hover:text-gray-700 underline" data-id="element-10">
          No thanks, I'll pass on the savings
        </button>
      </div>
    </div>;
}